import { useState, useMemo } from 'react';
import {
  useTaskTemplatesWithCount,
  useCreateTaskTemplate,
  useUpdateTaskTemplate,
  useDeleteTaskTemplate,
} from '../../../../hooks/useTaskTemplates';
import { ITEMS_PER_PAGE } from '../constants/taskTemplatesConstants';
import { usePaginatedFilter } from '../../../../common/hooks/usePaginatedFilter';

/**
 * Hook personalizado para gestionar el estado y lógica de la tabla de plantillas de tareas
 * Sigue los patrones establecidos en el proyecto
 */
export const useTaskTemplatesTable = () => {
  // Estados locales
  const [templateType, setTemplateType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Queries principales
  const { data: allTemplates = [], isLoading } = useTaskTemplatesWithCount();

  // Mutations
  const createTemplateMutation = useCreateTaskTemplate();
  const updateTemplateMutation = useUpdateTaskTemplate();
  const deleteTemplateMutation = useDeleteTaskTemplate();

  const {
    data,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    setPage,
  } = usePaginatedFilter({
    data: allTemplates,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: (collection, term) => {
      let filtered = collection;
      const normalizedTerm = term.trim().toLowerCase();

      if (normalizedTerm) {
        filtered = filtered.filter(
          (template) =>
            template.name?.toLowerCase().includes(normalizedTerm) ||
            template.description?.toLowerCase().includes(normalizedTerm)
        );
      }

      if (templateType) {
        filtered = filtered.filter((template) => template.template_type === templateType);
      }

      return filtered;
    },
  });

  const handleFilter = (name, value) => {
    if (name === 'search') {
      handleSearch(value);
    }
    if (name === 'templateType') {
      setTemplateType(value);
      setPage(1);
    }
  };

  // Funciones de manejo de diálogos
  const handleOpenCreateDialog = () => {
    setSelectedTemplate(null);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (template) => {
    setSelectedTemplate(template);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleOpenTasksDialog = (template) => {
    setSelectedTemplate(template);
    setTasksDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTemplate(null);
    setEditMode(false);
  };

  const handleCloseTasksDialog = () => {
    setTasksDialogOpen(false);
    setSelectedTemplate(null);
  };

  // Funciones de CRUD
  const handleSaveTemplate = async (templateData) => {
    try {
      if (editMode && selectedTemplate) {
        await updateTemplateMutation.mutateAsync({
          id: selectedTemplate.uuid,
          data: templateData,
        });
      } else {
        await createTemplateMutation.mutateAsync(templateData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await deleteTemplateMutation.mutateAsync(templateId);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  };

  const filter = useMemo(
    () => ({
      search: searchTerm,
      templateType,
    }),
    [searchTerm, templateType]
  );

  return {
    // Estados de datos
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,

    // Estados de diálogos
    dialogOpen,
    tasksDialogOpen,
    selectedTemplate,
    editMode,

    // Estados de loading de mutations
    isSaving: createTemplateMutation.isPending || updateTemplateMutation.isPending,
    isDeleting: deleteTemplateMutation.isPending,

    // Funciones de manejo
    handleFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenCreateDialog,
    handleOpenEditDialog,
    handleOpenTasksDialog,
    handleCloseDialog,
    handleCloseTasksDialog,
    handleSaveTemplate,
    handleDeleteTemplate,
  };
};
